import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Box,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Filter,
  Loader2,
  Search,
  Tag,
} from 'lucide-react';
import { adsApi } from '@/api/client';
import { downloadTextFile, reportDateRangeFilenamePart, rowsToCsv } from '@/api/csvExport';
import { apiErrorMessage } from '@/components/analysis/reportAnalysisUi';
import { NWordDistributionChart } from '@/components/nWords/NWordDistributionChart';
import {
  N_WORD_COLUMNS,
  WORD_LENGTHS,
  wordLengthLabel,
} from '@/components/nWords/nWordShare';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAccountCurrency } from '@/contexts/AccountCurrencyContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { WORKSPACE_PAGE_SIZE_OPTIONS, type WorkspacePageSize } from '@/hooks/useServerPagination';
import type { NWordRow, NWordSortField, NWordSummary } from '@/types/nWords';
import { formatCurrency } from '@/utils/formatCurrency';
import { cn } from '@/utils/cn';

const REQUIRED_COLUMNS: NWordSortField[] = ['phrase', 'wordCount'];
const EXPORT_PAGE_SIZE = 1000;

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function formatPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

function formatRatio(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(2);
}

function rowKey(row: NWordRow): string {
  return `${row.wordCount}\n${row.phrase}`;
}

function cellValue(row: NWordRow, columnId: NWordSortField): string | number | null {
  if (columnId === 'wordCount') return wordLengthLabel(row.wordCount);
  return row[columnId];
}

function formatCell(
  row: NWordRow,
  columnId: NWordSortField,
  money: (value: number) => string
): string {
  if (columnId === 'phrase') return row.phrase;
  if (columnId === 'wordCount') return wordLengthLabel(row.wordCount);
  if (columnId === 'spend' || columnId === 'sales') return money(row[columnId]);
  if (columnId === 'acos' || columnId === 'cvr' || columnId === 'ctr') return formatPercent(row[columnId]);
  if (columnId === 'roi') return formatRatio(row.roi);
  if (columnId === 'cpc' || columnId === 'cpo') {
    const value = row[columnId];
    return value == null ? '—' : money(value);
  }
  return formatCount(row[columnId]);
}

export function NWordsPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { range } = useAppDateRange();
  const currency = useAccountCurrency();
  const { showToast } = useToast();
  const startDate = range.current.startDate;
  const endDate = range.current.endDate;

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [wordCounts, setWordCounts] = useState<number[]>([...WORD_LENGTHS]);
  const [sortBy, setSortBy] = useState<NWordSortField>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<WorkspacePageSize>(10);
  const [columns, setColumns] = useState<NWordSortField[]>(N_WORD_COLUMNS.map((column) => column.id));
  const [allSelected, setAllSelected] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const wordCountsKey = wordCounts.slice().sort((a, b) => a - b).join(',');

  useEffect(() => {
    setPage(1);
    setAllSelected(false);
    setSelected(new Set());
    setExcluded(new Set());
  }, [profileId, startDate, endDate, q, wordCountsKey]);

  const query = useQuery({
    queryKey: [
      'search-term-n-words',
      profileId,
      startDate,
      endDate,
      q,
      wordCountsKey,
      sortBy,
      sortDir,
      page,
      pageSize,
    ],
    queryFn: async () => {
      const res = await adsApi.getSearchTermNWords(profileId, {
        startDate,
        endDate,
        q: q || undefined,
        wordCounts: wordCounts.length === WORD_LENGTHS.length ? undefined : wordCounts,
        sortBy,
        sortDir,
        page,
        pageSize,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load n-words');
      return res.data;
    },
    enabled: Boolean(profileId && startDate && endDate),
    placeholderData: keepPreviousData,
  });

  const summary = query.data?.summary ?? [];
  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const visibleColumns = N_WORD_COLUMNS.filter((column) => columns.includes(column.id));
  const selectedCount = allSelected ? Math.max(0, total - excluded.size) : selected.size;

  const money = (value: number) =>
    formatCurrency(value, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function isChecked(key: string): boolean {
    return allSelected ? !excluded.has(key) : selected.has(key);
  }

  function toggleRow(key: string, checked: boolean) {
    if (allSelected) {
      setExcluded((prev) => {
        const next = new Set(prev);
        if (checked) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  const pageKeys = useMemo(() => (query.data?.rows ?? []).map(rowKey), [query.data?.rows]);
  const pageAllChecked = pageKeys.length > 0 && pageKeys.every((key) => isChecked(key));

  function togglePage(checked: boolean) {
    if (allSelected) {
      setExcluded((prev) => {
        const next = new Set(prev);
        for (const key of pageKeys) {
          if (checked) next.delete(key);
          else next.add(key);
        }
        return next;
      });
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      for (const key of pageKeys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }

  function toggleSort(field: NWordSortField) {
    if (sortBy === field) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'phrase' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  function toggleWordCount(wordCount: number) {
    setWordCounts((current) => {
      const has = current.includes(wordCount);
      if (has && current.length === 1) return current;
      return has ? current.filter((count) => count !== wordCount) : [...current, wordCount];
    });
  }

  async function exportRows() {
    setExporting(true);
    try {
      const collected: NWordRow[] = [];
      let nextPage = 1;
      let expected = 0;
      do {
        const res = await adsApi.getSearchTermNWords(profileId, {
          startDate,
          endDate,
          q: q || undefined,
          wordCounts: wordCounts.length === WORD_LENGTHS.length ? undefined : wordCounts,
          sortBy,
          sortDir,
          page: nextPage,
          pageSize: EXPORT_PAGE_SIZE,
        });
        if (!res.success || !res.data) throw new Error(res.message || 'Failed to export n-words');
        expected = res.data.total;
        collected.push(...res.data.rows);
        if (res.data.rows.length === 0) break;
        nextPage += 1;
      } while (collected.length < expected && nextPage <= 100);
      const csv = rowsToCsv(
        visibleColumns.map((column) => column.label),
        collected.map((row) => visibleColumns.map((column) => cellValue(row, column.id)))
      );
      downloadTextFile(
        `n-words_${reportDateRangeFilenamePart(startDate, endDate)}.csv`,
        csv
      );
    } catch (error) {
      showToast(apiErrorMessage(error, 'Could not export n-words'), 'error');
    } finally {
      setExporting(false);
    }
  }

  const summaryByCount = new Map(summary.map((row) => [row.wordCount, row]));
  const filterIsSubset = wordCounts.length < WORD_LENGTHS.length;

  return (
    <div className="min-h-full space-y-4 bg-page p-4 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-headline-md font-semibold text-foreground">N-word analysis</h1>
        <Button variant="secondary" type="button">
          <Box className="h-4 w-4" aria-hidden />
          Products
        </Button>
      </div>

      {query.error ? (
        <p className="text-sm text-error-ink">
          {apiErrorMessage(query.error, 'Could not load n-words')}
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="p-5">
          <h2 className="text-body-md font-semibold text-foreground">Word type performance</h2>
          <div className="mt-2">
            {WORD_LENGTHS.map((wordCount) => {
              const row: NWordSummary | undefined = summaryByCount.get(wordCount);
              return (
                <div
                  key={wordCount}
                  className="grid grid-cols-[7.5rem_1fr] items-center gap-3 border-b border-border py-4 last:border-b-0"
                >
                  <div>
                    <p className="font-medium text-foreground">{wordLengthLabel(wordCount)}</p>
                    <p className="text-xs text-foreground-muted">
                      {query.isLoading ? '—' : `${formatCount(row?.phraseCount ?? 0)} phrases`}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-right">
                    <div>
                      <p className="font-mono text-sm text-foreground">
                        {query.isLoading ? '—' : money(row?.sales ?? 0)}
                      </p>
                      <p className="text-[11px] text-foreground-subtle">Sales</p>
                    </div>
                    <div>
                      <p className="font-mono text-sm text-foreground">
                        {query.isLoading ? '—' : money(row?.spend ?? 0)}
                      </p>
                      <p className="text-[11px] text-foreground-subtle">Spend</p>
                    </div>
                    <div>
                      <p className="font-mono text-sm text-foreground">
                        {query.isLoading ? '—' : formatPercent(row?.acos ?? null)}
                      </p>
                      <p className="text-[11px] text-foreground-subtle">ACOS</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="text-body-md font-semibold text-foreground">Metrics distribution by word type</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Percentage share of each word length across key metrics
          </p>
          <div className="mt-4">
            <NWordDistributionChart summary={summary} />
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-subtle"
              aria-hidden
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search n-words..."
              aria-label="Search n-words"
              className="h-9 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-body-md text-foreground placeholder:text-foreground-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="sm" type="button">
                <Filter className="h-3.5 w-3.5" aria-hidden />
                Filters{filterIsSubset ? ` (${wordCounts.length})` : ''}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-44 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle">
                Word length
              </p>
              <div className="space-y-1.5">
                {WORD_LENGTHS.map((wordCount) => (
                  <label key={wordCount} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={wordCounts.includes(wordCount)}
                      onChange={() => toggleWordCount(wordCount)}
                    />
                    {wordLengthLabel(wordCount)}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <ReportColumnPicker
              columns={N_WORD_COLUMNS}
              selected={columns}
              onChange={setColumns}
              requiredColumnIds={REQUIRED_COLUMNS}
              panelTitle="Columns"
            />
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => void exportRows()}
              disabled={exporting || !profileId}
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Download className="h-3.5 w-3.5" aria-hidden />
              )}
              Export
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-muted/40 px-3 py-2">
          <button
            type="button"
            className="text-sm font-medium text-brand-deep hover:underline disabled:opacity-50"
            disabled={total === 0}
            onClick={() => {
              setAllSelected(true);
              setSelected(new Set());
              setExcluded(new Set());
            }}
          >
            Select All ({formatCount(total)})
          </button>
          <button
            type="button"
            className="text-sm text-foreground-muted hover:text-foreground disabled:opacity-50"
            disabled={selectedCount === 0}
            onClick={() => {
              setAllSelected(false);
              setSelected(new Set());
              setExcluded(new Set());
            }}
          >
            Deselect All
          </button>
          <span className="text-sm text-foreground-muted">
            {formatCount(selectedCount)} of {formatCount(total)} selected
          </span>
          <Button variant="secondary" size="sm" type="button" className="ml-auto">
            <Tag className="h-3.5 w-3.5" aria-hidden />
            Add Tags ({formatCount(selectedCount)})
          </Button>
        </div>

        <TableWrap>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-40 w-12 bg-surface-header">
                  <input
                    type="checkbox"
                    aria-label="Select rows on this page"
                    checked={pageAllChecked}
                    disabled={rows.length === 0}
                    onChange={(event) => togglePage(event.target.checked)}
                  />
                </TableHead>
                {visibleColumns.map((column) => {
                  const active = sortBy === column.id;
                  const frozen = column.id === 'phrase';
                  return (
                    <TableHead
                      key={column.id}
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className={cn(
                        'bg-surface-header',
                        frozen && 'sticky left-12 z-40 min-w-[12rem]',
                        column.id !== 'phrase' && column.id !== 'wordCount' && 'text-right'
                      )}
                    >
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1 uppercase',
                          column.id !== 'phrase' && column.id !== 'wordCount' && 'ml-auto'
                        )}
                        onClick={() => toggleSort(column.id)}
                      >
                        {column.label}
                        <span className="text-[10px] text-foreground-subtle" aria-hidden>
                          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <tbody>
              {query.isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="py-10 text-center text-foreground-muted">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
                    Loading n-words…
                  </TableCell>
                </TableRow>
              ) : query.isError && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="py-10 text-center text-foreground-muted">
                    Could not load n-words.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="py-10 text-center text-foreground-muted">
                    {q || filterIsSubset ? 'No n-words match these filters.' : 'No n-words in this date range.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  return (
                    <TableRow key={key}>
                      <TableCell className="sticky left-0 z-10 w-12 bg-surface">
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.phrase}`}
                          checked={isChecked(key)}
                          onChange={(event) => toggleRow(key, event.target.checked)}
                        />
                      </TableCell>
                      {visibleColumns.map((column) => {
                        const frozen = column.id === 'phrase';
                        const numeric = column.id !== 'phrase' && column.id !== 'wordCount';
                        return (
                          <TableCell
                            key={column.id}
                            className={cn(
                              frozen && 'sticky left-12 z-10 min-w-[12rem] bg-surface font-medium',
                              numeric && 'text-right font-mono'
                            )}
                          >
                            {formatCell(row, column.id, money)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableWrap>

        <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-2">
          <label className="flex items-center gap-2 text-sm text-foreground-muted">
            Rows per page
            <select
              value={pageSize}
              aria-label="Rows per page"
              onChange={(event) => {
                setPageSize(Number(event.target.value) as WorkspacePageSize);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-surface px-2 text-sm text-foreground"
            >
              {WORKSPACE_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span className="text-sm text-foreground-muted">
            Showing {formatCount(total)} of {formatCount(total)} n-words
          </span>
          <div className="ml-auto flex items-center gap-1">
            <span className="mr-2 text-sm text-foreground-muted">
              Page {Math.min(page, pageCount)} of {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              aria-label="First page"
              disabled={page <= 1}
              onClick={() => setPage(1)}
            >
              <ChevronsLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              aria-label="Next page"
              disabled={page >= pageCount}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              aria-label="Last page"
              disabled={page >= pageCount}
              onClick={() => setPage(pageCount)}
            >
              <ChevronsRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
